var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from "lit";
import { customElement, property, query, queryAssignedElements, } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { withinThreshold } from "../utilities/within-threshold";
import { relativePosition } from "../utilities/relative-position";
import { rotateArray } from "../utilities/array-rotate";
import { animations } from "./wh-carousel.stacked-animations";
let WhCarouselElement = class WhCarouselElement extends LitElement {
    constructor() {
        super(...arguments);
        this.debounceWaitTime = 50;
        this.runScrollHandler = ({ target }) => {
            this.scrollHandler(target);
        };
        this.snapPoints = [];
        this.active = 0;
        this.variant = "scrolling";
        this.isInitialized = false;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.carousel.removeEventListener("scroll", this.runScrollHandler);
    }
    firstUpdated() {
        this.processSnapPoints();
        this.carousel.addEventListener("scroll", this.runScrollHandler, {
            passive: true,
        });
        if (this.areSnapPointsSetInternally()) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.dispatchScrollCompleteEvent(this.snapPoints[0], true);
        }
        this.isInitialized = true;
    }
    updated(changedProperties) {
        if (this.active === undefined) {
            return;
        }
        if (this.variant === "scrolling" && this.snapPoints.length === 0) {
            return;
        }
        if (this.variant === "scrolling") {
            this.carousel.scrollTo({
                behavior: "smooth",
                left: this.snapPoints[this.active]?.position,
            });
        }
        if (this.variant === "stacked") {
            this.rotateStackedCarousel(this.active, this.calculateDirection(changedProperties.get("active"), this.active));
        }
    }
    dispatchScrollCompleteEvent(activeSnapPoint, firstUpdated = false) {
        this.snapPoints = this.snapPoints.map((point) => ({
            active: point.index === activeSnapPoint.index,
            element: point.element,
            index: point.index,
            last: point.last,
            position: point.position,
        }));
        const eventData = {
            activeIndex: activeSnapPoint.index,
            activeSnapPoint,
            firstUpdated,
            snapPoints: this.snapPoints,
            type: "scrollcomplete",
        };
        this.dispatchEvent(new CustomEvent("scrollcomplete", {
            detail: eventData,
        }));
    }
    processSnapPoints() {
        this.snapPoints = this.snapPointElements.map((point, index, points) => ({
            active: index === 0,
            element: point,
            index,
            last: index === points.length - 1,
            position: relativePosition(point, this.carousel).left,
        }));
    }
    rotateStackedCarousel(amount, direction) {
        const rotated = rotateArray(this.carouselElements, amount);
        const stagedOffset = this.carouselElements.length > 0
            ? // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                this.carouselElements[0]?.clientWidth ?? 0 / 2
            : undefined;
        const { active, hidden, post, pre } = animations(direction, stagedOffset);
        rotated.forEach((page, index) => {
            switch (index) {
                case 0: {
                    page.animate(pre.keyframes, pre.config);
                    break;
                }
                case 1: {
                    page.animate(active.keyframes, active.config);
                    break;
                }
                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                case 2: {
                    page.animate(post.keyframes, post.config);
                    break;
                }
                default: {
                    page.animate(hidden.keyframes, hidden.config);
                    break;
                }
            }
        });
    }
    scrollHandler(target) {
        if (this.debounceWaitTimeout !== undefined) {
            clearTimeout(this.debounceWaitTimeout);
        }
        this.debounceWaitTimeout = setTimeout(() => {
            const currentPoint = this.snapPoints.find(
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            (point) => point.position === target.scrollLeft);
            if (currentPoint) {
                this.dispatchScrollCompleteEvent(currentPoint);
            }
            else if (withinThreshold(this.carousel.scrollWidth - this.carousel.scrollLeft, this.carousel.clientWidth, 
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            3)) {
                const lastPoint = this.snapPoints.find((point) => point.last);
                if (lastPoint) {
                    this.dispatchScrollCompleteEvent(lastPoint);
                }
            }
        }, this.debounceWaitTime);
    }
    calculateDirection(oldValue, newValue) {
        const oldNumber = Number(oldValue);
        const newNumber = Number(newValue);
        if (oldNumber > newNumber) {
            return "previous";
        }
        if (newNumber > oldNumber) {
            return "next";
        }
        return "direct";
    }
    areSnapPointsSetInternally() {
        return this.snapPoints.length > 0;
    }
    renderVariantScrolling() {
        return html `
      <article
        class=${classMap({ scrolling: true, snappy: this.isInitialized })}
      >
        <slot @slotchange=${this.processSnapPoints}></slot>
      </article>
    `;
    }
    renderVariantStacked() {
        return html `
      <article class="stacked">
        <slot
          @slotchange=${() => {
            this.rotateStackedCarousel(0, "direct");
        }}
        ></slot>
      </article>
    `;
    }
    render() {
        if (this.variant === "stacked") {
            return this.renderVariantStacked();
        }
        return this.renderVariantScrolling();
    }
};
/* Stryker disable next-line StringLiteral */
WhCarouselElement.styles = css `
    :host {
      --wh-dimension-spacer-xxs: 8px;
    }

    article.scrolling {
      display: flex;
      flex-direction: row;
      gap: var(--wh-dimension-spacer-xxs);
      overflow: scroll;
      scroll-behavior: smooth;
      scrollbar-width: none;
    }

    .snappy {
      scroll-snap-stop: always;
      scroll-snap-type: x mandatory;
    }

    article.scrolling > ::slotted(section[wh-carousel-snap]) {
      scroll-snap-align: start;
    }

    article.stacked {
      display: flex;
      justify-content: center;
      position: relative;
    }

    article.stacked > ::slotted(section) {
      box-sizing: border-box;
      opacity: 0;
      position: absolute;
      transform-origin: center;
    }

    ::-webkit-scrollbar {
      height: 0;
      width: 0;
      background: transparent;
    }
  `;
__decorate([
    property({ attribute: "active" })
], WhCarouselElement.prototype, "active", void 0);
__decorate([
    property({ attribute: "variant" })
], WhCarouselElement.prototype, "variant", void 0);
__decorate([
    property({ type: Boolean })
], WhCarouselElement.prototype, "isInitialized", void 0);
__decorate([
    query("article")
], WhCarouselElement.prototype, "carousel", void 0);
__decorate([
    queryAssignedElements({ selector: "section" })
], WhCarouselElement.prototype, "carouselElements", void 0);
__decorate([
    queryAssignedElements({ selector: "[wh-carousel-snap]" })
], WhCarouselElement.prototype, "snapPointElements", void 0);
WhCarouselElement = __decorate([
    customElement("wh-carousel")
], WhCarouselElement);
export { WhCarouselElement };
